import ko from "knockout";
import ObservableModel from "util/ObservableModel";
import communicationMixin from "models/comunicationMixin";
import Participant from "models/Participant";
import screenMonitoringUtils from "util/screenMonitoringUtils";
import liveDataService from "services/liveDataService";
import LOG from "services/loggingService";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";

/**
 * @constructor
 * @this ScreenMonitor
 * Observable representation of a screen monitoring conversation. Tracks screen monitoring
 * state for conversations. The screen monitor object that is returned from the server
 * is a conversation object with participants. Each participant may have a screenMonitorings
 * array with the following structure:
 *
 * <pre><code>
 * {
 *     id: "conversation-id",
 *     participants: [
 *         {
 *             id: "participant-id",
 *             screenMonitorings: [
 *                 {
 *                     id: "screen-monitoring-id",
 *                     state: "connected|disconnected",
 *                     targetUser: {
 *                         id: "user-id-being-monitored"
 *                     },
 *                     screenMonitoring: {
 *                         id: "unique-screen-monitoring-session-id"
 *                     },
 *                     monitoringType: "AgentLevel|ConversationLevel",
 *                     count: 1
 *                 }
 *             ]
 *         }
 *     ]
 * }
 * </code></pre>
 *
 * @return {ScreenMonitor} a screen monitor conversation
 */
const ScreenMonitor = ObservableModel.extend(
    {
        defaults: {
            participants: [] as any[],
        },
        modelProperties: {
            participants: Participant,
        },
    },
    function (this: any) {
        const self = this;

        communicationMixin(self); // Adds functionality shared between different communication types.

        /**
         * A helper method to determine if this is a user screen monitor
         *
         * @return {Boolean} true if it is a user screen monitor, otherwise false.
         */
        self.isUserScreenMonitor = ko.computed(function () {
            if (self.participantForCurrentUser()) {
                return self.participantForCurrentUser().isUser();
            }
            return false;
        });

        /**
         * Returns whether or not this screen monitor is a monitoring session
         *
         * @return {Boolean} true if the screen monitor is monitoring, otherwise false
         */
        self.isScreenMonitor = ko.computed(function () {
            const participant = self.participantForCurrentUser();
            if (!participant) {
                return false;
            }
            const screenMonitorings = participant.screenMonitorings;
            const targetUserId =
                screenMonitoringUtils.getConnectedTargetUserId(
                    screenMonitorings
                );
            return targetUserId !== null;
        });

        /**
         * A helper method to determine if the screen monitoring session is in an active status
         *
         * @return {Boolean} true if the screen monitoring is active, false otherwise.
         */
        self.isActive = ko.computed(function () {
            const participant = self.participantForCurrentUser();
            if (!participant) {
                return false;
            }

            // Screen monitoring is active if status is in a connected state and has targetUser.id from ScreenMonitoring
            const screenMonitorings = participant.screenMonitorings;
            const targetUserId =
                screenMonitoringUtils.getConnectedTargetUserId(
                    screenMonitorings
                );
            return !self.isDisconnected() && targetUserId !== null;
        });
    }
);

// Conditionally register topic when feature toggle is enabled
// Use observable pattern to react to feature toggle changes
const screenMonitoringToggle = featureToggleService.getFeatureToggle(
    FeatureToggle.WEM_437_SCREEN_MONITORING
);

// Subscribe to feature toggle changes and register topic when enabled
let topicRegistered = false;
screenMonitoringToggle.subscribe(function (isEnabled: boolean) {
    if (isEnabled && !topicRegistered) {
        try {
            liveDataService.registerTopic("screenMonitors", ScreenMonitor);
            topicRegistered = true;
        } catch (error) {
            LOG.error("Error subscribing to screen monitoring topic", error);
        }
    }
});

export default ScreenMonitor;
